import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useAppContextNonNull } from "@contexts/AppContext";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import EndpointsEnum from "@models/EndpointsEnum";
import ICategory from "@models/ICategory";
import IGetCategoriesDTO from "@models/DTO/IGetCategoriesDTO";
import IOnGetCategoriesDTO from "@models/DTO/IOnGetCategoriesDTO";
import FeedbackService, { FEEDBACK_SOURCE_FILTER_BAR } from "@services/FeedbackService";
import { toQueryStringParts } from "@utils/apiQueryString";
import { useFilterPresets } from "@hooks/shared/useFilterPresets";
import { useCategoryResolver } from "@hooks/shared/useSocketLookups";
import {
    buildCatalogItemsFilterSections,
    CATALOG_ITEMS_CATEGORY_GUID_KEY,
    CATALOG_ITEMS_DEFAULT_FILTERS,
} from "@config/filters/catalogItemsFilters";
import FilterBar from "../../../shared/FilterBar/FilterBar";
import { useUrlFilters } from "../../../shared/FilterBar/useUrlFilters";

export const CATALOG_ITEMS_FILTER_ACTIONS_ID = "catalog-items-filter-actions";

/** The Catalog Overview URL carries only filters (sort + pagination live in screen state). */
const NON_FILTER_PARAMS: string[] = [];

type Props = {
    catalogGuid: string;
    isSearching: boolean;
    /**
     * Existing Catalog Overview fetch: receives the queryString filter `a=b` parts (the screen appends sort)
     * and the category guid, which `getCatalogItems` takes as its own socket field.
     */
    searchFn: (filterParts: string[], categoryGuid?: string) => void;
};

/**
 * Catalog Overview integration of the shared FilterBar (v2 search UI).
 * Screen key is ScreenOptionsEnum.CATALOGS ("catalogs"), which is also the presets JSON key.
 */
export const CatalogItemsFilterBar = (props: Props) => {
    const { catalogGuid, searchFn } = props;
    const { userModel, supplierModel, vendorModel, socket, uiContext } = useAppContextNonNull();
    const intl = useIntl();
    const navigate = useNavigate();
    const categoryLookup = useCategoryResolver();

    const [rootCategories, setRootCategories] = useState<ICategory[]>([]);

    const formatMessage = useCallback(
        (id: string) => (intl.messages && intl.messages[id] !== undefined ? intl.formatMessage({ id }) : id),
        [intl]
    );

    // Root categories for the drill-down picker (same request as the legacy CatalogItemFilter).
    useEffect(() => {
        const onGetCategories = (data: IOnGetCategoriesDTO) => {
            // The event is shared with the category sidebars; only root lists belong in the picker.
            if (
                !data.onlyRootCategories ||
                data.isOrderCategory ||
                data.isTakeOffCategory ||
                data.isVendorCategory ||
                data.isHomeCategories
            ) {
                return;
            }

            setRootCategories(data.categories.filter((c) => !c.hide_category));
        };

        socket.on(EndpointsEnum.CategoriesOnGetCategories, onGetCategories);

        const payload: IGetCategoriesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            catalogGuid,
            onlyRootCategories: true,
        };

        socket.emit(EndpointsEnum.CategoriesGetCategories, payload);

        return () => {
            socket.removeListener(EndpointsEnum.CategoriesOnGetCategories, onGetCategories);
        };
    }, [catalogGuid]);

    const sections = useMemo(
        () =>
            buildCatalogItemsFilterSections({
                categories: rootCategories,
                hasNewReleases: !!supplierModel.has_new_releases_category,
                hasReadyToShip: !!supplierModel.has_ready_to_ship_category,
                hasCommonItems: !!supplierModel.show_common_items_modal,
                hasQbo: !!supplierModel.Supplier_Integration?.has_qbo,
                hasQbd: !!supplierModel.Supplier_Integration?.has_qbd,
                showHiddenFeatures: !!uiContext.showHiddenFeatures,
                lookups: categoryLookup,
                formatMessage,
            }),
        [rootCategories, supplierModel, uiContext.showHiddenFeatures, categoryLookup, formatMessage]
    );

    // Deep links (e.g. /catalogs/:guid?sku=ABC): read on mount and again on Back / Forward.
    const { initialFilters, historyKey, isFirstSearch } = useUrlFilters(sections, NON_FILTER_PARAMS);

    const { presets, onPresetsChange } = useFilterPresets(ScreenOptionsEnum.CATALOGS);

    const handleSearch = useCallback(
        (query: { [apiKey: string]: unknown }) => {
            // Keep the URL in sync so filtered views can be shared / deep-linked.
            // The mount search replaces (no extra history entry); user searches push.
            const urlParts = toQueryStringParts(query);

            navigate(
                { pathname: `/catalogs/${catalogGuid}`, search: urlParts.length ? `?${urlParts.join("&")}` : "" },
                { replace: isFirstSearch.current }
            );
            isFirstSearch.current = false;

            // The category guid is a socket field, not a queryString param.
            const { [CATALOG_ITEMS_CATEGORY_GUID_KEY]: categoryGuid, ...queryStringFilters } = query;

            searchFn(toQueryStringParts(queryStringFilters), categoryGuid ? String(categoryGuid) : undefined);
        },
        [navigate, searchFn, isFirstSearch, catalogGuid]
    );

    const handleFeedback = useCallback(
        async (message: string) => {
            try {
                await FeedbackService.sendFeedback(message, FEEDBACK_SOURCE_FILTER_BAR, { userModel, supplierModel, vendorModel });
                uiContext.notifyFn(null, { message: "feedback_sent", level: NotificationLevelEnum.success });
            } catch (error) {
                console.error("Failed to send FilterBar feedback", error);
                uiContext.notifyFn(null, { message: "feedback_failed", level: NotificationLevelEnum.error });
                throw error;
            }
        },
        [userModel, supplierModel, vendorModel, uiContext]
    );

    if (presets === null) {
        return null;
    }

    return (
        <FilterBar
            key={historyKey}
            sections={sections}
            presets={presets}
            onSearch={handleSearch}
            onPresetsChange={onPresetsChange}
            screenKey={ScreenOptionsEnum.CATALOGS}
            mode={"drawer"}
            defaultFilters={CATALOG_ITEMS_DEFAULT_FILTERS}
            initialFilters={initialFilters}
            allLabel={"all_catalog_items"}
            actionsContainerId={CATALOG_ITEMS_FILTER_ACTIONS_ID}
            isSearching={props.isSearching}
            onSendFeedback={handleFeedback}
        />
    );
};

export default CatalogItemsFilterBar;
